import React, { useState } from "react";
import clsx from "clsx";
import {
  Button,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  Typography
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { Add as AddIcon } from "@material-ui/icons";
import { ConfirmDialog, TableButton } from "../../dataDisplays";

const useStyles = makeStyles(theme => ({
  subTableWithTitle: {
    marginTop: theme.spacing(2),
    width: "100%"
  },
  subTable: {
    width: "100%"
  },
  placeholder: {
    padding: theme.spacing(2)
  }
}));

export type SimpleColumnType<T> = {
  label: React.ReactNode;
  renderCell: (data: T, idx: number) => React.ReactNode;

  width?: number;
  minWidth?: number;
  maxWidth?: number;
  align?: "left" | "center" | "right";
  style?: React.CSSProperties;
};

type Props<T> = {
  title?: React.ReactNode;
  data: T[];
  columns: SimpleColumnType<T>[];
  showIndex: boolean;
  onClickAdd?: () => void;
  onClickEdit?: (data: T, idx: number) => void;
  onClickDelete?: (data: T, idx: number) => void;
  onMoveUp?: (idx: number) => void;
  onMoveDown?: (idx: number) => void;
  subRowRenderer?: (data: T, idx: number) => React.ReactNode;
  readonly?: boolean;
  style?: React.CSSProperties;
};

const indexColumnDef: SimpleColumnType<unknown> = {
  label: "#",
  align: "center",
  width: 60,
  renderCell: (_, idx) => idx + 1
};

export function SimpleSubTablePlaceholder(props: {
  loading?: boolean;
  text?: string;
  className?: string;
}) {
  const classes = useStyles();
  const { loading, text, className } = props;
  return (
    <Paper className={clsx(className ?? classes.subTable, classes.placeholder)}>
      {loading && (
        <>
          <Typography color="textSecondary" variant="overline">
            Loading...
          </Typography>
          <LinearProgress />
        </>
      )}
      {text && <Typography color="textSecondary">{text}</Typography>}
    </Paper>
  );
}

export default function SimpleSubTable<T>(props: Props<T>) {
  const classes = useStyles();

  const {
    title,
    data,
    columns,
    showIndex,
    onClickAdd,
    onClickEdit,
    onClickDelete,
    onMoveUp,
    onMoveDown,
    subRowRenderer,
    readonly,
    style
  } = props;

  const btnCount =
    (onClickEdit ? 1 : 0) +
    (onClickAdd ? 1 : 0) +
    (onMoveUp ? 1 : 0) +
    (onMoveDown ? 1 : 0);
  const hasManage = !readonly && btnCount > 0;
  const manageCellWidth = 38 * btnCount + 16;
  const shouldRenderNoData = (!onClickAdd || readonly) && data.length === 0;

  const [confirmDialogState, setConfirmDialogState] = useState<{
    open: boolean;
    onConfirm?: () => void;
  }>({ open: false });
  const handleCloseDialog = () => {
    setConfirmDialogState({ open: false, onConfirm: undefined });
  };

  const renderingColumns = showIndex ? [indexColumnDef, ...columns] : columns;
  const cellStyle = (column: SimpleColumnType<T>) => ({
    ...column.style,
    minWidth: column.minWidth ?? column.width,
    maxWidth: column.maxWidth,
    width: column.width
  });

  return (
    <>
      {title &&
        (typeof title === "string" ? (
          <Typography variant="h4" gutterBottom>
            {title} ({data.length})
          </Typography>
        ) : (
          title
        ))}

      <TableContainer
        component={Paper}
        style={style}
        className={title ? classes.subTableWithTitle : classes.subTable}
      >
        <Table>
          <TableHead>
            <TableRow>
              {hasManage && (
                <TableCell
                  style={{
                    width: manageCellWidth,
                    minWidth: manageCellWidth
                  }}
                  align="center"
                >
                  จัดการ
                </TableCell>
              )}
              {renderingColumns.map((col, i) => (
                <TableCell key={i} align={col.align} style={cellStyle(col)}>
                  {col.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((d, idx) => {
              const subRowComponent = subRowRenderer
                ? subRowRenderer(d, idx)
                : undefined;
              return (
                <React.Fragment key={idx}>
                  <TableRow>
                    {hasManage && (
                      <TableCell
                        style={{
                          width: manageCellWidth,
                          minWidth: manageCellWidth,
                          padding: 0
                        }}
                        align="center"
                      >
                        {onMoveUp && (
                          <TableButton
                            type="up"
                            onClick={() => onMoveUp(idx)}
                            style={{ marginRight: 8 }}
                            disabled={idx === 0}
                          />
                        )}
                        {onMoveDown && (
                          <TableButton
                            type="down"
                            onClick={() => onMoveDown(idx)}
                            style={{ marginRight: 8 }}
                            disabled={idx === data.length - 1}
                          />
                        )}
                        {onClickEdit && (
                          <TableButton
                            type="edit"
                            onClick={() => onClickEdit(d, idx)}
                            style={{ marginRight: 8 }}
                          />
                        )}
                        {onClickDelete && (
                          <TableButton
                            type="delete"
                            onClick={() => {
                              setConfirmDialogState({
                                open: true,
                                onConfirm: () => {
                                  onClickDelete(d, idx);
                                  handleCloseDialog();
                                }
                              });
                            }}
                          />
                        )}
                      </TableCell>
                    )}
                    {renderingColumns.map((col, i) => (
                      <TableCell
                        key={i}
                        align={col.align}
                        style={cellStyle(col)}
                      >
                        {col.renderCell(d, idx)}
                      </TableCell>
                    ))}
                  </TableRow>
                  {subRowComponent && (
                    <TableRow>
                      {hasManage && <TableCell />}
                      <TableCell
                        colSpan={renderingColumns.length}
                        padding="none"
                      >
                        {subRowComponent}
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              );
            })}
            {shouldRenderNoData && (
              <TableRow>
                <TableCell
                  colSpan={renderingColumns.length + (hasManage ? 1 : 0)}
                  align="center"
                >
                  <Typography variant="body2" color="textSecondary">
                    ไม่มีรายการ
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
          {onClickAdd && !readonly && (
            <TableFooter>
              <TableRow>
                <TableCell
                  colSpan={renderingColumns.length + (hasManage ? 1 : 0)}
                  align="center"
                >
                  <Button
                    variant="text"
                    color="primary"
                    startIcon={<AddIcon />}
                    onClick={onClickAdd}
                  >
                    เพิ่ม
                  </Button>
                </TableCell>
              </TableRow>
            </TableFooter>
          )}
        </Table>
      </TableContainer>

      {onClickDelete && !readonly && (
        <ConfirmDialog
          title={`ยืนยันการลบรายการ`}
          open={confirmDialogState.open}
          onClose={handleCloseDialog}
          onConfirm={confirmDialogState.onConfirm}
        />
      )}
    </>
  );
}
